
import { supabase } from "@/integrations/supabase/client";
import { CampingSite, SiteAmenities, SiteLocation, SiteType, CampingSitesResponse } from "./types";
import { transformSiteWithAmenities } from "./transformers";

/**
 * Lists camping sites based on provided filters
 */
export async function listCampingSites({
  siteType,
  location,
  active = true,
  startDate,
  endDate
}: {
  siteType?: SiteType;
  location?: SiteLocation;
  active?: boolean;
  startDate?: Date;
  endDate?: Date;
}): Promise<CampingSitesResponse> {
  try {
    // 1. Fetch camping sites with basic filters
    // Status-aware: show Active and Coming Soon publicly; hide Maintenance / Seasonal / Inactive.
    // The legacy `active` parameter is intentionally ignored — status is the source of truth.
    void active;
    let query = supabase
      .from('camping_sites')
      .select('*')
      .in('status', ['active', 'coming_soon']);
    
    if (siteType) {
      // Only pass 'rv' or 'wild-horse' to the database query
      // as those are the only valid values in the site_type enum
      if (siteType === 'rv' || siteType === 'wild-horse') {
        query = query.eq('site_type', siteType);
      }
      // For 'tent' type or any future types that aren't in the database enum,
      // we could apply custom filtering logic here if needed
    }
    
    if (location) {
      query = query.eq('location', location);
    }
    
    const { data: sites, error: sitesError } = await query;
    
    if (sitesError) {
      console.error('Error fetching camping sites:', sitesError);
      throw new Error(sitesError.message);
    }
    
    console.log("Raw Supabase Sites Data:", sites);
    
    if (sites.length === 0) {
      return { sites: [], amenities: {} };
    }
    
    // 2. If date range is provided, filter available sites
    let availableSiteIds: string[] = [];
    if (startDate && endDate) {
      console.log(`Checking availability for date range: ${startDate.toISOString()} to ${endDate.toISOString()}`);
      const formattedStartDate = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
      const formattedEndDate = endDate.toISOString().split('T')[0]; // YYYY-MM-DD
      
      // Get list of available site IDs for the date range
      const { data: availableIds, error: availabilityError } = await supabase.rpc(
        'get_available_sites_for_dates',
        {
          check_start_date: formattedStartDate,
          check_end_date: formattedEndDate,
          site_type: siteType ? siteType : null,
          site_location: location ? location : null
        }
      );
      
      if (availabilityError) {
        console.error('Error checking site availability:', availabilityError);
        // Continue without filtering for availability if there's an error
      } else if (availableIds) {
        availableSiteIds = availableIds;
        console.log(`Found ${availableSiteIds.length} available sites for the date range`);
      }
    }
    
    // 3. Fetch site amenities separately
    const siteIds = sites.map(site => site.id);
    const { data: amenitiesData, error: amenitiesError } = await supabase
      .from('site_amenities')
      .select('*')
      .in('site_id', siteIds);
    
    if (amenitiesError) {
      console.error('Error fetching site amenities:', amenitiesError);
      throw new Error(amenitiesError.message);
    }
    
    console.log("Raw Supabase Amenities Data:", amenitiesData);
    
    // ADDITIONAL LOGGING: Let's specifically check the electric_50amp and amperage values
    if (location === 'sheep-creek') {
      console.log("SHEEP CREEK SITES ELECTRICAL DATA:");
      amenitiesData?.forEach(amenity => {
        const matchingSite = sites.find(s => s.id === amenity.site_id);
        console.log(`Site ${matchingSite?.site_number} (ID: ${amenity.site_id}):`, {
          electric_50amp: amenity.electric_50amp,
          amperage: amenity.amperage,
          site_has_electricity: matchingSite?.has_electricity
        });
      });
    }
    
    // 4. Create an amenities lookup object by site_id
    const amenitiesMap: Record<string, SiteAmenities> = {};
    
    amenitiesData?.forEach(amenity => {
      if (amenity && amenity.site_id) {
        amenitiesMap[amenity.site_id] = {
          amperage: amenity.amperage,
          electric_50amp: amenity.electric_50amp,
          sewage: amenity.sewage,
          max_rv_length: amenity.max_rv_length,
          petsAllowed: amenity.pets_allowed,
          wifi: amenity.wifi,
          back_in: amenity.back_in,
          pull_thru: amenity.pull_thru,
          gravel_surface: amenity.gravel_surface,
          paved: amenity.paved,
          shade: amenity.shade,
          shade_morning: amenity.shade_morning,
          shade_afternoon: amenity.shade_afternoon,
          fire_pit: amenity.fire_pit,
          grill: amenity.grill,
          near_lake: amenity.near_lake,
          near_restroom: amenity.near_restroom,
          picnic_table: amenity.picnic_table
        };
      }
    });
    
    // 5. Transform the sites data
    let transformedSites: CampingSite[] = sites.map(site => {
      // Get amenities for this site or use empty object if not found
      const siteAmenity = amenitiesMap[site.id] || {};
      
      // Extra debugging for Sheep Creek sites
      if (site.location === 'sheep-creek') {
        console.log(`Processing Sheep Creek site ${site.site_number}:`, {
          hasElectricity: site.has_electricity,
          rawAmenities: siteAmenity
        });
      }
      
      // Transform the site with amenities
      const transformedSite = transformSiteWithAmenities(site, siteAmenity);
      
      // Add availability information if we have date filtering
      if (startDate && endDate && availableSiteIds.length > 0) {
        transformedSite.available = availableSiteIds.includes(site.id);
      }
      
      return transformedSite;
    });
    
    // If we're checking availability, filter out unavailable sites
    if (startDate && endDate && availableSiteIds.length > 0) {
      transformedSites = transformedSites.filter(site => site.available);
    }
    
    return { 
      sites: transformedSites,
      amenities: amenitiesMap
    };
  } catch (error) {
    console.error('Error in listCampingSites:', error);
    throw error;
  }
}

/**
 * Gets a specific camping site by ID
 */
export async function getCampingSiteById(siteId: string): Promise<CampingSite> {
  try {
    let site = null;
    let siteError = null;

    // First, try to query by UUID (most common case)
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(siteId);
    
    if (isUUID) {
      // Query by UUID
      const { data: siteData, error: uuidError } = await supabase
        .from('camping_sites')
        .select('*')
        .eq('id', siteId)
        .single();
      
      site = siteData;
      siteError = uuidError;
    }
    
    // If UUID query failed or siteId is not a UUID, try querying by site_number
    if (!site) {
      const { data: siteData, error: siteNumberError } = await supabase
        .from('camping_sites')
        .select('*')
        .eq('site_number', siteId)
        .single();
      
      site = siteData;
      siteError = siteNumberError;
    }
    
    // If both attempts failed, log the error and throw
    if (siteError || !site) {
      console.error('Error fetching camping site:', {
        siteId,
        isUUID,
        error: siteError
      });
      throw new Error(`Site not found: ${siteId}`);
    }

    // Query 2: Get amenities for the site
    const { data: siteAmenitiesData, error: amenitiesError } = await supabase
      .from('site_amenities')
      .select('*')
      .eq('site_id', site.id)
      .maybeSingle();
    
    if (amenitiesError) {
      console.error('Error fetching site amenities:', amenitiesError);
      // Don't throw here - we can still return the site without amenities
    }
    
    // Log the data for debugging
    console.log(`Site ${site.site_number} Full Data Debug:`, {
      siteId: site.id,
      siteType: site.site_type,
      location: site.location,
      hasElectricity: site.has_electricity,
      hasWater: site.has_water,
      rawAmenities: siteAmenitiesData,
      amenityDetails: {
        amperage: siteAmenitiesData?.amperage,
        electric_50amp: siteAmenitiesData?.electric_50amp,
        electric: site.has_electricity
      }
    });
    
    // For safety, create a default empty amenities object
    const siteAmenities: SiteAmenities = siteAmenitiesData || {};
    
    // Return the transformed site data
    return transformSiteWithAmenities(site, siteAmenities);
  } catch (error) {
    console.error('Error in getCampingSiteById:', error);
    throw error;
  }
}

/**
 * Checks if a site is available for a specific date range
 */
export async function checkSiteAvailability(
  siteId: string,
  startDate: Date,
  endDate: Date
): Promise<boolean> {
  try {
    const formattedStartDate = startDate.toISOString().split('T')[0];
    const formattedEndDate = endDate.toISOString().split('T')[0];
    
    const { data: isAvailable, error } = await supabase.rpc(
      'check_site_availability_for_dates',
      {
        site_id: siteId,
        check_start_date: formattedStartDate,
        check_end_date: formattedEndDate
      }
    );
    
    if (error) {
      console.error('Error checking site availability:', error);
      throw new Error(error.message);
    }
    
    return isAvailable;
  } catch (error) {
    console.error('Error in checkSiteAvailability:', error);
    throw error;
  }
}
